import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../firebase/firebase';
import { PRODUCT_COLLECTION } from '../utills/productCollection';

const COL = PRODUCT_COLLECTION; // 'zapee_restaurant_products'
const productCollection = collection(db, COL);

// ── Storage helpers ──────────────────────────────────────────────

export async function uploadProductImage(file: File, docId: string): Promise<string> {
  const path = `product/${docId}_${file.name}`;
  const imgRef = storageRef(storage, path);
  await uploadBytes(imgRef, file);
  return getDownloadURL(imgRef);
}

export async function deleteStorageUrl(url: string): Promise<void> {
  if (!url || !url.includes('firebasestorage')) return;
  try {
    const match = url.match(/\/o\/(.+?)\?/);
    if (!match) return;
    await deleteObject(storageRef(storage, decodeURIComponent(match[1])));
  } catch (e) {
    console.warn('Storage delete skipped:', e);
  }
}

// A price variant (Small / Medium / Large …) — aligned with the restaurant app.
export interface ProductVariant {
  variantId?: string;
  name: string;
  price: number;
  isDefault?: boolean;
  available?: boolean;
  pricing?: { sellingPrice?: number; mrp?: number; currency?: string };
}

// Clean model aligned to the restaurant app schema (zapee_restaurant_products).
export interface ProductData {
  id?: string;
  tittle: string;          // product title (also written to `name`)
  description: string;
  image: string;           // main image (also written to `defaultImage`)
  featureImages: string[]; // gallery (also written to `images`/`gallery`)
  price: string;           // selling price
  discount: string;        // MRP (schema field is named `discount`; also `mrp`)
  stock: string;
  category: string;        // category ID (references the restaurant's categories)
  itemcategory: string;
  branch: string;          // restaurant id (also written to `restaurantId`)
  available: boolean;      // also mirrored to `show` + `isActive`
  show: boolean;
  isVeg: boolean;
  bestSeller: boolean;
  latest: boolean;
  variants: ProductVariant[];
  addOns?: any[];
  timestamp: string;
  // ── mirrors written for cross-app compatibility (not edited directly) ──
  name?: string;
  defaultImage?: string;
  images?: string[];
  gallery?: string[];
  isActive?: boolean;
  mrp?: string;
  restaurantId?: string;
}

const str = (v: any): string =>
  v === undefined || v === null ? '' : String(v);

const num = (v: any): number => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Normalise a variant entry written by either app into a common shape.
function normVariant(v: any): ProductVariant {
  const pricing = v?.pricing ?? {};
  return {
    variantId: str(v?.variantId ?? v?.id) || undefined,
    name: str(v?.name),
    price: num(pricing.sellingPrice ?? v?.price),
    isDefault: !!v?.isDefault,
    available: v?.available ?? true,
    pricing: {
      sellingPrice: num(pricing.sellingPrice ?? v?.price),
      mrp: num(pricing.mrp ?? v?.price),
      currency: pricing.currency ?? 'INR',
    },
  };
}

// Normalise a raw Firestore doc (which may have been written by the restaurant
// app with slightly different field names) into the admin's ProductData shape.
function normalize(id: string, d: any): ProductData {
  const gallery: string[] = Array.isArray(d.featureImages)
    ? d.featureImages
    : Array.isArray(d.images)
      ? d.images
      : Array.isArray(d.gallery)
        ? d.gallery
        : [];
  const available = d.available ?? d.show ?? d.isActive ?? true;
  const variants: ProductVariant[] = Array.isArray(d.variants)
    ? d.variants.map(normVariant)
    : [];
  return {
    ...d,
    id,
    tittle: str(d.tittle ?? d.name),
    name: str(d.name ?? d.tittle),
    image: str(d.image ?? d.defaultImage),
    defaultImage: str(d.defaultImage ?? d.image),
    featureImages: gallery,
    images: gallery,
    gallery,
    available,
    show: d.show ?? available,
    isActive: d.isActive ?? available,
    isVeg: d.isVeg ?? true,
    bestSeller: !!d.bestSeller,
    latest: !!d.latest,
    category: str(d.category),
    itemcategory: str(d.itemcategory ?? d.itemCategory),
    price: str(d.price),
    discount: str(d.discount ?? d.mrp),
    mrp: str(d.mrp ?? d.discount),
    stock: str(d.stock),
    branch: str(d.branch ?? d.restaurantId),
    restaurantId: str(d.restaurantId ?? d.branch),
    description: str(d.description),
    variants,
  } as ProductData;
}

// Mirror admin fields onto the restaurant app's expected field names so a
// product saved here is fully readable by the restaurant + customer apps.
function toSchema(data: Partial<ProductData>): Record<string, any> {
  const out: Record<string, any> = { ...data };
  if (data.tittle !== undefined) out.name = data.tittle;
  if (data.image !== undefined) out.defaultImage = data.image;
  if (data.featureImages !== undefined) {
    out.images = data.featureImages;
    out.gallery = data.featureImages;
  }
  if (data.available !== undefined) {
    out.show = data.available;
    out.isActive = data.available;
  }
  if (data.discount !== undefined) out.mrp = data.discount; // discount == MRP
  if (data.branch !== undefined) out.restaurantId = data.branch;
  out.updatedAt = serverTimestamp();
  return out;
}

export async function createProduct(productData: ProductData) {
  try {
    const data = toSchema(productData);
    data.createdAt = serverTimestamp();
    const docRef = await addDoc(productCollection, data);
    console.log('Product created with ID: ', docRef.id);
    return docRef.id;
  } catch (e) {
    console.error('Error creating product: ', e);
    throw e;
  }
}

export async function getProducts(): Promise<ProductData[]> {
  const snapshot = await getDocs(productCollection);
  return snapshot.docs.map(d => normalize(d.id, d.data()));
}

export async function getProductsByRestaurant(storeuid: string): Promise<ProductData[]> {
  if (!storeuid) return getProducts();
  // Products may carry the restaurant id under `branch` or `restaurantId`.
  const [byBranch, byRestaurantId] = await Promise.all([
    getDocs(query(productCollection, where('branch', '==', storeuid))),
    getDocs(query(productCollection, where('restaurantId', '==', storeuid))),
  ]);
  const byId = new Map<string, ProductData>();
  for (const d of [...byBranch.docs, ...byRestaurantId.docs]) {
    byId.set(d.id, normalize(d.id, d.data()));
  }
  return Array.from(byId.values());
}

export async function updateProduct(id: string, updateData: Partial<ProductData>) {
  try {
    await updateDoc(doc(db, COL, id), toSchema(updateData));
  } catch (e) {
    console.error('Error updating product: ', e);
    throw e;
  }
}

export async function deleteProduct(id: string, imageUrl?: string, featureImages?: string[]) {
  try {
    await deleteDoc(doc(db, COL, id));
    if (imageUrl) await deleteStorageUrl(imageUrl);
    for (const url of featureImages ?? []) await deleteStorageUrl(url);
  } catch (e) {
    console.error('Error deleting product: ', e);
    throw e;
  }
}
